/**
 * Microsoft sign-in via MSAL.js - gets a real Microsoft ID token for
 * POST /api/auth/sso-login, which avrio-backend's SsoTokenVerifier verifies
 * against Microsoft's own JWKS (matching its multi-tenant issuer PATTERN,
 * not one fixed issuer string - see SsoTokenVerifier.microsoftIssuerValidator)
 * before trusting a single claim out of it.
 *
 * Unlike Google Identity Services, MSAL's loginPopup() is a plain imperative
 * call: it opens a real OAuth popup window directly, and calling it from
 * this app's own button's onClick (a genuine user gesture) is exactly the
 * supported pattern - no renderButton()/ghost-button workaround needed the
 * way Google's credential flow requires.
 */

import { PublicClientApplication, type AuthenticationResult } from '@azure/msal-browser';
import { MICROSOFT_AUTHORITY } from './ssoClientConfig';

export class MicrosoftSignInError extends Error {}

let msalInstance: PublicClientApplication | null = null;
let initializePromise: Promise<void> | null = null;

const getMsalInstance = (clientId: string): PublicClientApplication => {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication({
      auth: {
        clientId,
        authority: MICROSOFT_AUTHORITY,
        // This app's own origin - MSAL redirects/popups back here. Matches
        // the pattern avrio-backend's own OAuth-adjacent redirect URLs use
        // (window.location.origin), not a hardcoded host, so this is
        // correct in every environment (localhost, staging, prod) without
        // per-environment config.
        redirectUri: window.location.origin,
      },
    });
  }
  return msalInstance;
};

/** Resolves with a real Microsoft ID token (JWT). */
export const signInWithMicrosoft = async (clientId: string): Promise<string> => {
  const instance = getMsalInstance(clientId);

  // initialize() is required before any other MSAL call as of msal-browser
  // v3+, and must only run once per instance - a second call throws.
  if (!initializePromise) {
    initializePromise = instance.initialize();
  }
  await initializePromise;

  let result: AuthenticationResult;
  try {
    result = await instance.loginPopup({ scopes: ['openid', 'profile', 'email'] });
  } catch (err) {
    throw new MicrosoftSignInError(
      err instanceof Error ? err.message : 'Microsoft sign-in did not complete.',
    );
  }

  if (!result.idToken) {
    throw new MicrosoftSignInError('Microsoft sign-in did not return an ID token.');
  }
  return result.idToken;
};
